import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import Icon from 'components/AppIcon';

const ConfigEditor = ({ config, onUpdate }) => {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (config) {
      setFormData({
        standard_processing_fee: config.standard_processing_fee || '1.50',
        donation_processing_fee: config.donation_processing_fee || '1.50',
        standard_transportation: config.standard_transportation || '3500.00',
        donation_transportation: config.donation_transportation || '3500.00',
        rag_trader_rate: config.rag_trader_rate || '0.30',
        value_channel_rate: config.value_channel_rate || '1.00',
        default_tax_rate: config.default_tax_rate || '20.00',
        default_cogs_placeholder: config.default_cogs_placeholder || '8.00',
        notes: config.notes || '',
      });
    }
  }, [config]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const { data, error } = await supabase
        .from('calculation_config')
        .update({
          ...formData,
          last_updated_at: new Date().toISOString(),
        })
        .eq('id', 1)
        .select();

      if (error) throw error;

      setMessage({ type: 'success', text: 'Configuration updated successfully!' });
      if (onUpdate) onUpdate(data[0]);

      // Clear success message after 3 seconds
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error updating config:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to update configuration' });
    } finally {
      setSaving(false);
    }
  };

  const configFields = [
    {
      label: 'Standard Processing Fee',
      name: 'standard_processing_fee',
      type: 'number',
      unit: '$/lb',
      description: 'Cost per pound for standard textile processing',
      icon: 'DollarSign',
      step: '0.01',
    },
    {
      label: 'Donation Model Processing Fee',
      name: 'donation_processing_fee',
      type: 'number',
      unit: '$/lb',
      description: 'Cost per pound for donation model processing',
      icon: 'DollarSign',
      step: '0.01',
    },
    {
      label: 'Standard Transportation Cost',
      name: 'standard_transportation',
      type: 'number',
      unit: '$',
      description: 'Fixed shipping cost for standard process',
      icon: 'Truck',
      step: '0.01',
    },
    {
      label: 'Donation Model Transportation Cost',
      name: 'donation_transportation',
      type: 'number',
      unit: '$',
      description: 'Fixed shipping cost for donation model',
      icon: 'Truck',
      step: '0.01',
    },
    {
      label: 'Rag Trader Revenue Rate',
      name: 'rag_trader_rate',
      type: 'number',
      unit: '$/lb',
      description: 'Revenue per pound from rag trader channel',
      icon: 'Recycle',
      step: '0.01',
    },
    {
      label: 'Value Channel Revenue Rate',
      name: 'value_channel_rate',
      type: 'number',
      unit: '$/unit',
      description: 'Revenue per unit from value channel',
      icon: 'TrendingUp',
      step: '0.01',
    },
    {
      label: 'Default Corporate Tax Rate',
      name: 'default_tax_rate',
      type: 'number',
      unit: '%',
      description: 'Default tax rate pre-filled in form',
      icon: 'Percent',
      step: '0.01',
    },
    {
      label: 'Default COGS Placeholder',
      name: 'default_cogs_placeholder',
      type: 'number',
      unit: '$',
      description: 'Suggested COGS per unit (placeholder)',
      icon: 'Tag',
      step: '0.01',
    },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Message Alert */}
      {message.text && (
        <div
          className={`flex items-center space-x-2 p-4 rounded-lg border ${
            message.type === 'success'
              ? 'bg-success-50 border-success-200 text-success'
              : 'bg-error-50 border-error-200 text-error'
          }`}
        >
          <Icon
            name={message.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
            size={20}
          />
          <span className="text-sm font-body-medium">{message.text}</span>
        </div>
      )}

      {/* Configuration Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {configFields.map((field) => (
          <div key={field.name} className="space-y-2">
            <label
              htmlFor={field.name}
              className="block text-sm font-body-medium text-text-primary"
            >
              <div className="flex items-center space-x-2">
                <Icon name={field.icon} size={16} className="text-primary" />
                <span>{field.label}</span>
                <span className="text-xs text-text-tertiary">({field.unit})</span>
              </div>
            </label>
            <input
              id={field.name}
              type={field.type}
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              step={field.step}
              required
              className="block w-full px-4 py-3 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-150 bg-surface text-text-primary"
            />
            <p className="text-xs text-text-tertiary">{field.description}</p>
          </div>
        ))}
      </div>

      {/* Notes Field */}
      <div className="space-y-2">
        <label
          htmlFor="notes"
          className="block text-sm font-body-medium text-text-primary"
        >
          <div className="flex items-center space-x-2">
            <Icon name="FileText" size={16} className="text-primary" />
            <span>Notes</span>
          </div>
        </label>
        <textarea
          id="notes"
          value={formData.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={3}
          placeholder="Optional notes about this configuration..."
          className="block w-full px-4 py-3 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-150 bg-surface text-text-primary resize-none"
        />
      </div>

      {/* Submit Button */}
      <div className="flex items-center justify-end space-x-4 pt-6 border-t border-border">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-lg font-body-medium hover:bg-primary-600 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-elevation-1"
        >
          {saving ? (
            <>
              <div className="animate-spin mr-2">
                <Icon name="Loader2" size={20} />
              </div>
              Saving...
            </>
          ) : (
            <>
              <Icon name="Save" size={20} className="mr-2" />
              Save Configuration
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default ConfigEditor;
